import { Button, Modal, Tooltip } from "antd";
import { message } from "utils/ciq-message";
import CIQDatePicker from "components/custom-date-picker";
import DatePickerIcon from "components/svg-icons/datepicker-icon";
import GrayDatePickerIcon from "components/svg-icons/gray-datepicker-icon";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import type { Dayjs } from "dayjs";
import { useCallback, useContext, useMemo, useState } from "react";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";

import "./feature-status.scss";
import { EWorkflowStatusDataBlock } from "constants/index";
import { FeatureTypes } from "entity-app/constants";
import { APPLY_WORKFLOW_MUTATION } from "entity-app/shared-components/entity-dateblock/queries/mutation";

type TMilestone = {
  id: string;
  sequence_no: number;
  name: string;
  actual: null | string;
  actual_date: null | string;
};

// Only these feature types can be started ahead of their predecessors, and only
// from the details page. Absence from this map means the log never blocks.
const EARLY_RELEASE_BLOCKED_MSG: { [key: number]: string } = {
  [FeatureTypes.MATERIALS]:
    "Linked predecessors have not been completed. To release the Material ahead of schedule, go to Material Details.",
  [FeatureTypes.BID_PACKAGE]:
    "Linked Design Packages have not been completed. To initiate an Early Bid Package, go to Bid Package Details."
};

export default function FeatureLogSetStatusUI(props: {
  refetchLogData?: (...args: any[]) => any;
  // Bid package rows carry no date_block; their log supplies the type instead.
  featureType?: FeatureTypes;
  data: {
    id: string;
    auto_inc_value: number;
    sequence_id: number;
    title: string;
    milestones: Array<TMilestone> | undefined;
    date_block:
      | {
          milestones: Array<TMilestone>;
          id: string;
          feature_type?: FeatureTypes;
        }
      | undefined;
    workflow_status: EWorkflowStatusDataBlock;
    linked_items?: Array<{
      workflow_status: EWorkflowStatusDataBlock;
      feature_type: FeatureTypes;
    }>;
    feature_workflow_instance_id?: string;
    ready_for_release?: boolean;
  };
}) {
  const { data, refetchLogData } = props;
  const featureType = data?.date_block?.feature_type ?? props.featureType;
  const sequenceId = data?.auto_inc_value || data?.sequence_id;
  const headerTitle = `${sequenceId}-${data?.title}`;

  const [visible, setVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(DateUtils.dateTimeObj());
  const { gqlClientForProject } = useContext(ProjectContext);

  const milestoneInfo = useMemo(() => {
    const milestones = data?.milestones || data?.date_block?.milestones || [];

    const sortedMilestones = milestones
      .slice()
      .sort((a, b) => a.sequence_no - b.sequence_no);
    const nextMilestone = sortedMilestones.find(
      (x) => !x.actual_date && !x.actual
    );

    let currentMilestone: TMilestone | undefined;

    if (nextMilestone && nextMilestone.sequence_no > 1) {
      const sequenceNo = nextMilestone.sequence_no - 1;
      currentMilestone = milestones.find((x) => x.sequence_no === sequenceNo);
    }

    return { currentMilestone, nextMilestone };
  }, [data?.date_block?.milestones, data?.milestones]);

  const [applyWorkflowMutation, { loading }] = useCIQMutation(
    APPLY_WORKFLOW_MUTATION,
    {
      client: gqlClientForProject
    }
  );

  const disabledActualDate = useCallback(
    (current: Dayjs) => {
      const after = DateUtils.dateTimeObj(current).isAfter(
        DateUtils.dateTimeObj()
      );

      const currentActualDate =
        milestoneInfo.currentMilestone?.actual_date ||
        milestoneInfo.currentMilestone?.actual;

      if (!currentActualDate) return after;

      const currDateFromPicker = DateUtils.dateTimeObj(current)
        .hour(13)
        .minute(0)
        .second(0);
      const before = currDateFromPicker.isBefore(
        DateUtils.dateTimeObj(currentActualDate)
      );
      return before || after;
    },
    [milestoneInfo.currentMilestone]
  );

  const isEntityWorkflowCompleted = useMemo(() => {
    return !milestoneInfo.nextMilestone;
  }, [milestoneInfo.nextMilestone]);

  const onConfirm = async () => {
    const saveDate = DateUtils.formatDateWithLunchTime(selectedDate);

    const variables = {
      input: [
        {
          feature_instance_id: data?.id,
          workflow_instance_id:
            data.feature_workflow_instance_id || data?.date_block?.id,
          milestones: [
            { actual_date: saveDate, id: milestoneInfo.nextMilestone?.id }
          ]
        }
      ],
      metadata: {
        request_source: "INLINE_EDIT_LOG_PAGE"
      }
    };

    const updateResponse = await applyWorkflowMutation({ variables });

    if (updateResponse.success) {
      message.success("Updated successfully.");
      setVisible(false);
      if (refetchLogData) setTimeout(refetchLogData, 1000);
    } else {
      message.error("Failed to update.");
    }
  };

  // Starting one of these ahead of its predecessors is a details-page-only
  // action, so the log's date picker is replaced by an explanatory icon.
  const blockedByPredecessors =
    EARLY_RELEASE_BLOCKED_MSG[featureType as number] !== undefined &&
    !data?.ready_for_release &&
    data?.workflow_status !== EWorkflowStatusDataBlock.IN_PROGRESS;

  const tooltipTitle = useMemo(() => {
    if (blockedByPredecessors) {
      return EARLY_RELEASE_BLOCKED_MSG[featureType as number];
    }
    if (isEntityWorkflowCompleted === true) {
      return "Workflow completed";
    }

    return "Update status";
  }, [isEntityWorkflowCompleted, blockedByPredecessors, featureType]);

  return (
    <div className="flex ">
      <Tooltip title={tooltipTitle}>
        {isEntityWorkflowCompleted === true || blockedByPredecessors ? (
          <div className="flex w-[46px] justify-center">
            <GrayDatePickerIcon />
          </div>
        ) : (
          <Button
            className="flex space-x-2 items-center !border-none !bg-transparent disabled:bg-slate-600"
            onClick={() => setVisible(true)}
            data-testid="featureinstancestatus-button-datepicker"
          >
            <DatePickerIcon />
          </Button>
        )}
      </Tooltip>
      <Modal
        title={
          <div
            className="px-3"
            data-testid="featureinstancestatus-modal"
          >{`Update Status of ${headerTitle}`}</div>
        }
        styles={{ body: { height: 330 } }}
        open={visible}
        destroyOnHidden
        onOk={onConfirm}
        okText="Confirm"
        okButtonProps={{ disabled: loading, loading }}
        cancelButtonProps={{ disabled: loading }}
        onCancel={() => {
          setVisible(false);
        }}
        width={391}
      >
        <div className="flex flex-col feature-status-popover-content justify-center space-y-1">
          <div className=" text-xs w-full text-left">
            <span className="font-semibold uppercase">
              {milestoneInfo.nextMilestone?.name}
            </span>{" "}
            on {DateUtils.format(selectedDate, DATE_FORMAT_MMDDYYYY)}
          </div>
          <CIQDatePicker
            value={selectedDate}
            className="w-full"
            onChange={(val: any) => {
              setSelectedDate(val);
            }}
            open
            getPopupContainer={(trigger: any) => {
              return trigger;
            }}
            showToday={false}
            disabledDate={disabledActualDate}
          />
        </div>
      </Modal>
    </div>
  );
}
